import React from "react";
import { getDriveItemSharepointUniqueId } from "../../../src/taskpane/services/GraphService";
import { getUserEffectivePermissions } from "../../../src/taskpane/services/SharePointRestServices";
import { IOneDriveRootDetails } from "../../../src/taskpane/interfaces/IOneDriveRootDetails";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import authModule from "../../../utilities/AuthModule";
import { logger } from "../../../utilities/logger";
import {
  getDirectoryPermissionIdentity,
  getDirectoryRequestKey,
  isOneDriveDirectory,
} from "../helpers/contentManagerDirectoryIdentity";
import {
  CurrentFolderPermissionState,
  initialCurrentFolderPermissionState,
} from "../helpers/currentFolderPermissionState";
import { createLoadCurrentFolderPermissionsDependencies } from "../helpers/createLoadCurrentFolderPermissionsDependencies";
import { loadCurrentFolderPermissions, LoadCurrentFolderPermissionsError } from "../helpers/loadCurrentFolderPermissions";

export const useCurrentFolderPermissions = (
  currentDirectory?: IPageSettings,
  oneDriveRootDetails?: IOneDriveRootDetails
): CurrentFolderPermissionState => {
  const [permissionState, setPermissionState] = React.useState<CurrentFolderPermissionState>(initialCurrentFolderPermissionState);
  const requestSequence = React.useRef(0);

  React.useEffect(() => {
    let cancelled = false;
    const currentRequestSequence = ++requestSequence.current;

    if (!currentDirectory) {
      setPermissionState(initialCurrentFolderPermissionState);
      return () => {
        cancelled = true;
      };
    }

    const baseIdentity = getDirectoryPermissionIdentity({
      currentDirectory,
      oneDriveRootDetails,
    });
    const directoryRequestKey = getDirectoryRequestKey({
      currentDirectory,
      oneDriveRootDetails,
    });

    if (isOneDriveDirectory(currentDirectory) && !oneDriveRootDetails?.sharePointSiteUrl) {
      setPermissionState({
        directoryRequestKey,
        identityKey: baseIdentity.key,
        status: "loading",
      });

      return () => {
        cancelled = true;
      };
    }

    setPermissionState({
      directoryRequestKey,
      identityKey: baseIdentity.key,
      status: "loading",
    });

    (async () => {
      try {
        const nextPermissionState = await loadCurrentFolderPermissions({
          currentDirectory,
          oneDriveRootDetails,
          dependencies: createLoadCurrentFolderPermissionsDependencies({
            authModule,
            getDriveItemSharepointUniqueIdFn: getDriveItemSharepointUniqueId,
            getUserEffectivePermissionsFn: getUserEffectivePermissions,
          }),
        });

        if (cancelled || currentRequestSequence !== requestSequence.current) {
          return;
        }

        setPermissionState(nextPermissionState);
      } catch (error) {
        if (cancelled || currentRequestSequence !== requestSequence.current) {
          return;
        }

        if (error instanceof LoadCurrentFolderPermissionsError) {
          logger(error.cause, "useCurrentFolderPermissions.ts (load permissions)");
          setPermissionState(error.state);
          return;
        }

        logger(error, "useCurrentFolderPermissions.ts (load permissions)");

        setPermissionState({
          directoryRequestKey,
          identityKey: baseIdentity.key,
          status: "failed",
          errorMessage: "Could not load folder permissions.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentDirectory?.id,
    currentDirectory?.parentId,
    currentDirectory?.sharePointListId,
    currentDirectory?.sharePointUniqueId,
    currentDirectory?.type,
    currentDirectory?.webUrl,
    currentDirectory?.directory,
    currentDirectory?.serverRelativePath,
    oneDriveRootDetails?.sharePointListId,
    oneDriveRootDetails?.sharePointSiteUrl,
  ]);

  return permissionState;
};
